import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Typography,
} from '@material-ui/core';
import PolicyNumberInput from 'ApplicationEntry/components/WorkFlow/PolicyNumberInput';
import {changeProductType, getProducts} from 'ApplicationEntry/ducks/caseDetails';
import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import {CaseDetails, getProductFamilyByIndex, getProductFamilyGroups} from 'ApplicationEntry/models/CaseDetails';
import {ValidGroupCaseDetails} from 'ApplicationEntry/models/GroupCaseDetails';
import {ValidOrdinaryCaseDetails} from 'ApplicationEntry/models/OrdinaryCaseDetails';
import {PolicyNumber} from 'ApplicationEntry/models/PolicyNumber';
import {
  isErrorLoadingPolicyTypes,
  isLoadedPolicyTypes,
  isUnloadedPolicyTypes,
  LoadedPolicyTypes,
  PolicyTypes,
} from 'ApplicationEntry/models/PolicyTypes';
import {Product} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import Alert from 'Shared/components/Alert';

type PolicyTypesCheckboxesProps = {
  applicationType: ApplicationType;
  policyTypes: PolicyTypes;
  maxAllowedProductTypes: number;
  policyNumber: PolicyNumber;
  caseDetails: CaseDetails;
};

const PolicyTypesCheckboxes = (props: PolicyTypesCheckboxesProps) => {
  const {applicationType, policyTypes: retrievedPolicyTypes, maxAllowedProductTypes} = props;

  const [selectedProductGroups, setSelectedProductGroups] = useState<Product[]>([]);
  const dispatch = useDispatch();

  let groupCaseDetails = props.caseDetails as ValidGroupCaseDetails;
  let ordinaryCaseDetails = props.caseDetails.key == 'ValidOrdinaryCaseDetails' && props.caseDetails;

  const getProduct = (policyNo: string) => {
    let productCodeValue = policyNo.charAt(policyNo.length - 1);
    let companyCodeValue = policyNo.substring(0, 2);
    if (productCodeValue == '7') {
      return 'Critical Illness';
    }
    if (productCodeValue == '2') {
      return 'Cancer';
    }
    if (productCodeValue == '4') {
      return 'Accident';
    }
    if (productCodeValue == '1') {
      return 'Disability';
    }
    if (companyCodeValue === '52' && productCodeValue == '0') {
      return 'Group Whole Life';
    }
    if (companyCodeValue === '51' && productCodeValue == 'U') {
      return 'Universal Life';
    }
    if (companyCodeValue === '51' && productCodeValue == '0') {
      return 'Group Term life';
    }
    if (productCodeValue == '5') {
      return 'Shop';
    }
    if (productCodeValue === 'W') {
      return 'Individual Whole Life';
    }
    return '';
  };

  useEffect(() => {
    if (!isLoadedPolicyTypes(retrievedPolicyTypes)) {
      dispatch(getProducts(applicationType));
    }
  }, [applicationType, dispatch, retrievedPolicyTypes]);

  if (isUnloadedPolicyTypes(retrievedPolicyTypes)) {
    return (
      <div className="case-details-section__policy-type-checkboxes">
        <CircularProgress />
      </div>
    );
  } else if (isErrorLoadingPolicyTypes(retrievedPolicyTypes)) {
    return (
      <div className="case-details-section__error-retrieving-products-message">
        <Alert message="An Error Occurred While Retrieving Available Product Information." />
      </div>
    );
  }
  const {availablePolicyTypes: policyTypes, selectedPolicyTypes} = retrievedPolicyTypes;
  const isAtMaxAllowedProductTypes = selectedPolicyTypes.length >= maxAllowedProductTypes;

  return (
    <div className="case-details-section__policy-type-checkboxes">
      <div className="case-details-section__sub-title-container">
        <Typography variant="subtitle1" className="case-details-section__sub-title-container-title">
          Policy Type(s)
        </Typography>
      </div>
      <FormControl component="fieldset">
        <div>
          <Grid container spacing={4}>
            {policyTypes.length > 0 ? (
              policyTypes.map((policy, index) => {
                return (
                  <>
                    {policy.active && (
                      <Grid item xs={12} md={3} xl={4} key={`grid-${policy.friendlyName!}-${index}`}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="checkedB"
                              color="secondary"
                              disabled={
                                isAtMaxAllowedProductTypes
                                  ? !selectedPolicyTypes.some(x => x.productGroup! === policy.productGroup!)
                                  : selectedProductGroups.some(x => x.productGroup! === policy.productGroup!)
                              }
                              checked={selectedPolicyTypes.some(x => x.productGroup! === policy.productGroup!)}
                              onChange={() => {
                                const grpIndex = getProductFamilyGroups().findIndex(group =>
                                  group.find(product => product.productGroup! === policy.productGroup!)
                                );
                                if (selectedPolicyTypes.some(x => x.productGroup! === policy.productGroup!)) {
                                  dispatch(
                                    changeProductType(
                                      new LoadedPolicyTypes(
                                        policyTypes,
                                        selectedPolicyTypes.filter(pt => pt.productGroup! !== policy.productGroup!)
                                      )
                                    )
                                  );

                                  if (grpIndex !== -1) {
                                    const unselectedFamilyProducts = getProductFamilyByIndex(grpIndex, policy);
                                    setSelectedProductGroups(
                                      selectedProductGroups.filter(
                                        product =>
                                          !unselectedFamilyProducts.some(x => x.productGroup! === product.productGroup!)
                                      )
                                    );
                                  }
                                } else {
                                  dispatch(
                                    changeProductType(
                                      new LoadedPolicyTypes(policyTypes, [...selectedPolicyTypes, policy])
                                    )
                                  );
                                  if (grpIndex !== -1) {
                                    setSelectedProductGroups(
                                      selectedProductGroups.concat(getProductFamilyByIndex(grpIndex, policy))
                                    );
                                  }
                                }
                              }}
                            />
                          }
                          label={
                            <Typography className="case-details-section__checkbox-label">
                              <span className="case-details-section__checkbox-label__information--name">
                                {policy.name!}
                              </span>
                              <span className="case-details-section__checkbox-label__information">
                                <small className="case-details-section__checkbox-label__information--classification">
                                  {policy.family?.familyType!}
                                </small>
                              </span>
                              <span className="case-details-section__checkbox-label__information">
                                <small className="case-details-section__checkbox-label__information--description">
                                  {policy.description!}
                                </small>
                              </span>
                            </Typography>
                          }
                          key={`checkbox-label-${policy.name!}-${index}`}
                        />
                      </Grid>
                    )}
                  </>
                );
              })
            ) : (
              <div className="case-details-section__error-retrieving-products-message">
                <Alert message="An Error Occurred While Retrieving Available Product Information." />
              </div>
            )}
            <Grid item xs={12}>
              <FormHelperText>*Max {maxAllowedProductTypes} Policy Types</FormHelperText>
              {/* <div>
                {groupCaseDetails?.disableNexttoShowOtherSectionArr?.map((item, idx) => {
                  if (item.policyNo && item.policyNumberExists) {
                    let productName = getProduct(item.policyNo);
                    return (
                      <FormHelperText className="case-details-section__duplicate-policy">
                        {' '}
                        {productName} Policy Number {item.policyNo} already exists.
                      </FormHelperText>
                    );
                  }
                })}
              </div> */}
              <div>
                {props.caseDetails.key == 'ValidGroupCaseDetails'
                  ? props.caseDetails?.disableNexttoShowOtherSectionArr?.map((item, idx) => {
                      if (props.caseDetails.key === 'ValidGroupCaseDetails') {
                        if (item.policyNo && item.policyNumberExists) {
                          let productName = getProduct(item.policyNo);
                          return (
                            <FormHelperText className="case-details-section__duplicate-policy">
                              {' '}
                              {productName} Policy Number {item.policyNo} already exists.
                            </FormHelperText>
                          );
                        }
                      }
                    })
                  : null}
              </div>
              <div>
                {props.caseDetails.key == 'ValidOrdinaryCaseDetails'
                  ? props.caseDetails?.disableNexttoShowOtherSectionArr?.map((item, idx) => {
                      if (props.caseDetails.key === 'ValidOrdinaryCaseDetails') {
                        if (item.policyNo && item.policyNumberExists) {
                          let productName = getProduct(item.policyNo);
                          return (
                            <FormHelperText className="case-details-section__duplicate-policy">
                              {' '}
                              {productName} Policy Number {item.policyNo} already exists.
                            </FormHelperText>
                          );
                        }
                      }
                    })
                  : null}
              </div>
            </Grid>

            <PolicyNumberInput
              policyNumber={props.policyNumber}
              selectedPolicyTypes={selectedPolicyTypes}
              applicationType={applicationType}
              caseDetails={props.caseDetails}
            />
          </Grid>
        </div>
      </FormControl>
    </div>
  );
};

export default PolicyTypesCheckboxes;
